import { Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

export const useTooltipStyles = makeStyles<Theme>(theme => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: '#0f172a',
    boxShadow: '0px 1px 0.5px rgba(0,0,0,0.05), 0px 0px 4px rgba(0,0,0,0.15)',
    borderRadius: '8px',
    padding: '16px',
    maxWidth: '300px',
    fontFamily: 'inherit',
  },
  arrow: {
    color: theme.palette.common.white,
    '&::before': {
      boxShadow: '0px 1px 0.5px rgba(0,0,0,0.05), 0px 0px 4px rgba(0,0,0,0.15)',
    },
  },
}));

export const useAgentContextStyles = makeStyles<Theme>(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    minHeight: 0,
  },
  root: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    minHeight: 0,
    background: theme.palette.common.white,
  },
  sidebar: {
    width: '240px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid #E2E8F0`,
    borderLeft: `1px solid #E2E8F0`,
    overflowY: 'auto',
    background: theme.palette.common.white,
  },
  sidebarProgress: {
    fontSize: '14px',
    color: '#757575',
    lineHeight: '16px',
    padding: '0 4px',
  },
  sidebarItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 3, 1, 6),
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: '22px',
    color: '#141414',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  sidebarItemActive: {
    color: theme.palette.primary.main,
  },
  sidebarItemIcon: {
    position: 'absolute',
    left: '24px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '16px',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sidebarItemIconComplete: {},
  sidebarItemLabel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Floats above the row as a rounded box on hover — does not sit in normal
  // flow, so it never affects sidebarItemLabel's width/truncation. overflow:
  // hidden clips the icon buttons to the box's rounded corners instead of
  // letting them spill past a too-tight background (looked like clipping).
  sidebarItemActions: {
    position: 'absolute',
    top: 0,
    right: theme.spacing(2),
    transform: 'translateY(-70%)',
    display: 'flex',
    alignItems: 'center',
    background: theme.palette.common.white,
    borderRadius: '4px',
    boxShadow: '0px 2px 8px rgba(19, 30, 64, 0.15)',
    padding: theme.spacing(0.5, 1),
    overflow: 'hidden',
    zIndex: 2,
    opacity: 0,
    pointerEvents: 'none',
    '& svg': {
      marginTop: '-8px',
    },
    '$sidebarItem:hover &': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  sidebarItemEditInput: {
    flex: 1,
    minWidth: 0,
    fontSize: '16px',
    lineHeight: '22px',
    fontFamily: 'inherit',
    color: '#141414',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: '4px',
    padding: '2px 6px',
    outline: 'none',
  },
  addTopic: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#1864fd',
    cursor: 'pointer',
    padding: theme.spacing(1.5, 3),
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: theme.palette.common.white,
  },
  editorTop: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid #E2E8F0`,
  },
  topicsScrollArea: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    paddingTop: theme.spacing(2),
  },
  editorHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  editorTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#141414',
    flex: 1,
  },
  editorActions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  tooltipHeader: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.palette.common.gray800,
    margin: '0 0 10px',
    fontFamily: 'inherit',
    borderBottom: `1px solid #E2E8F0`,
    paddingBottom: '8px',
  },
  tooltipQuestion: {
    fontSize: '14px',
    lineHeight: '20px',
    color: '#0f172a',
    margin: 0,
    fontFamily: 'inherit',
    fontWeight: 400,
  },
  editorActionButton: {
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '16px',
    color: theme.palette.common.gray800,
    padding: '8px 16px',
    minHeight: 'unset',
    borderRadius: '5px',
    border: `1px solid ${theme.palette.common.gray300}`,
    backgroundColor: theme.palette.common.white,
    boxShadow: `0px 1px 1px rgba(19,30,64,0.08)`,
    '&:hover': {
      backgroundColor: theme.palette.common.gray100,
      boxShadow: `0px 1px 1px rgba(19,30,64,0.08)`,
    },
    '& .MuiButton-startIcon': {
      marginRight: '6px',
    },
  },
  topicSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  topicSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  topicSectionTitle: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.common.gray800,
    lineHeight: '20px',
  },
  topicBadge: {},
  topicTextarea: {
    width: '100%',
    minHeight: '120px',
    padding: '12px 17px',
    borderRadius: '5px',
    border: `1px solid ${theme.palette.common.gray300}`,
    backgroundColor: theme.palette.common.white,
    fontFamily: 'inherit',
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.common.gray800,
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
    '&::placeholder': {
      color: theme.palette.common.gray500,
    },
    '&:focus': {
      borderColor: '#1864fd',
    },
  },
  topicExampleLink: {
    fontSize: '12px',
    lineHeight: '14px',
    color: '#1864fd',
    cursor: 'pointer',
    textDecoration: 'none',
    flexShrink: 0,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  processingBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    background: '#e7f3ff',
    border: '1px solid #357fef',
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: '20px',
    color: '#0f172a',
    boxShadow: '0px 1px 1px rgba(0,0,0,0.15)',
  },
  processingBannerIcon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  processingBannerText: { flex: 1 },
  processingBannerDismiss: {
    minWidth: 'unset',
    color: '#357fef',
    padding: '0 4px',
  },
  successBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    background: '#F0FDF4',
    border: '1px solid #86EFAC',
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: '20px',
    color: '#0f172a',
    boxShadow: '0px 1px 1px rgba(0,0,0,0.15)',
  },
  successBannerText: { flex: 1 },
  successBannerDismiss: {
    minWidth: 'unset',
    color: '#16A34A',
    padding: '0 4px',
  },
  topicSectionDisabled: {
    opacity: 0.5,
    pointerEvents: 'none' as const,
  },
  topicTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  topicInfoIcon: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    color: theme.palette.common.gray500,
    lineHeight: 0,
  },
  sidebarDisabled: {
    pointerEvents: 'none',
    opacity: 0.4,
    userSelect: 'none' as const,
  },
  progressWidgetOuter: {
    background: theme.palette.common.gray100,
    borderBottom: `1px solid #E2E8F0`,
  },
  progressCard: {
    background: theme.palette.common.white,
    boxShadow: '0px 1px 1px rgba(19,30,64,0.08)',
    padding: '16px 20px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  progressBarOuter: { padding: '0 4px' },
  progressBarTrack: {
    background: '#EBEBEB',
    borderRadius: '6px',
    height: '6px',
    position: 'relative',
  },
  progressBarFill: {
    background: '#1864fd',
    borderRadius: '6px',
    height: '6px',
    transition: 'width 0.3s ease',
  },
  newTopicInput: {
    fontSize: '14px',
    padding: '8px 24px',
    border: 'none',
    borderTop: `1px solid ${theme.palette.common.gray300}`,
    outline: 'none',
    width: '100%',
  },
  viewVersionHistoryButton: {
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '16px',
    color: '#1864fd',
    padding: '4px 8px',
    minHeight: 'unset',
    background: 'none',
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      background: 'none',
      boxShadow: 'none',
      textDecoration: 'underline',
    },
    '& .MuiButton-startIcon': {
      marginRight: '6px',
    },
  },
  versionHistoryPanel: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.common.white,
    overflow: 'hidden',
  },
  versionHistoryHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: `1px solid #E2E8F0`,
    flexShrink: 0,
  },
  versionHistoryTitle: {
    fontSize: '16px',
    fontWeight: 500,
    color: '#141414',
    flex: 1,
  },
  versionHistoryCloseBtn: {
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '2px',
    display: 'flex',
    alignItems: 'center',
    color: '#404d5f',
    lineHeight: 0,
    '&:hover': { color: '#141414' },
  },
  versionHistoryList: {
    flex: 1,
    overflowY: 'auto' as const,
  },
  versionHistoryEmpty: {
    padding: '24px 16px',
    fontSize: '14px',
    color: theme.palette.common.gray500,
    textAlign: 'center' as const,
  },
  versionHistoryEntry: {
    borderTop: `1px solid #E2E8F0`,
    padding: '8px 16px',
    cursor: 'pointer',
    overflow: 'hidden',
    minWidth: 0,
    '&:first-child': { borderTop: 'none' },
  },
  versionHistoryEntryActive: {
    backgroundColor: 'rgba(231, 243, 255, 0.5)',
  },
  versionHistoryEntryRow1: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '3px',
  },
  versionHistoryDate: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#0f172a',
    lineHeight: '20px',
    flex: 1,
  },
  versionHistoryEntryRow2: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '6px',
    marginBottom: '3px',
  },
  versionHistoryTopicName: {
    fontSize: '14px',
    fontWeight: 400,
    color: '#404d5f',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    flex: 1,
    lineHeight: '20px',
  },
  versionHistoryBadge: {
    fontSize: '11px',
    fontWeight: 500,
    padding: '1px 8px',
    borderRadius: '12px',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: '18px',
  },
  versionHistoryBadgeEdited: {
    background: '#e7f3ff',
    color: '#0c4497',
  },
  versionHistoryBadgeAdded: {
    background: '#d8f9d3',
    color: '#145b2f',
  },
  versionHistoryBadgeDeleted: {
    background: '#fff1f2',
    color: '#9f1239',
  },
  versionHistoryBadgeRestored: {
    background: '#f3f0ff',
    color: '#5b21b6',
  },
  versionHistoryEntryMenu: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  versionHistoryMenuBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '4px',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#404d5f',
    padding: 0,
    '&:hover': {
      background: '#e8eaed',
    },
  },
  versionHistoryDropdown: {
    position: 'absolute',
    right: 0,
    top: '28px',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    zIndex: 1000,
    minWidth: '180px',
    overflow: 'hidden',
  },
  versionHistoryDropdownItem: {
    display: 'block',
    width: '100%',
    padding: '10px 14px',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    fontSize: '14px',
    color: '#1a202c',
    cursor: 'pointer',
    '&:hover': {
      background: '#f7f8fa',
    },
    '&:disabled': {
      color: '#a0aec0',
      cursor: 'not-allowed',
    },
  },
  versionHistoryActor: {
    fontSize: '14px',
    fontStyle: 'italic',
    color: '#404d5f',
    lineHeight: '18px',
  },
  versionHistoryRestoredFrom: {
    fontSize: '12px',
    color: '#5b21b6',
    fontStyle: 'italic' as const,
  },
  versionHistoryDiff: {
    marginTop: '10px',
    borderRadius: '6px',
    border: '1px solid #E2E8F0',
    maxHeight: '200px',
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  versionHistoryDiffHeader: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: '#8899a6',
    padding: '6px 10px',
    background: '#f8f9fa',
    borderBottom: '1px solid #E2E8F0',
  },
  versionHistoryDiffBody: {
    padding: '8px 10px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  versionHistoryDiffRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  versionHistoryDiffLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#8899a6',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
  },
  versionHistoryDiffValue: {
    fontSize: '13px',
    color: '#1a202c',
    lineHeight: '18px',
    wordBreak: 'break-word' as const,
  },
  versionHistoryDiffOld: {
    background: '#fff5f5',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '13px',
    color: '#9f1239',
    lineHeight: '18px',
    wordBreak: 'break-word' as const,
    textDecoration: 'line-through',
    opacity: 0.8,
  },
  versionHistoryDiffNew: {
    background: '#f0fdf4',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '13px',
    color: '#145b2f',
    lineHeight: '18px',
    wordBreak: 'break-word' as const,
  },
  versionHistoryDiffAdded: {
    background: '#f0fdf4',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '13px',
    color: '#145b2f',
    lineHeight: '18px',
    wordBreak: 'break-word' as const,
  },
  versionHistoryDiffDeleted: {
    background: '#fff5f5',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '13px',
    color: '#9f1239',
    lineHeight: '18px',
    wordBreak: 'break-word' as const,
  },
  versionHistoryDiffRestored: {
    background: '#faf5ff',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '13px',
    color: '#5b21b6',
    lineHeight: '18px',
    wordBreak: 'break-word' as const,
  },
  versionHistoryRestoreNote: {
    fontSize: '11px',
    color: '#8899a6',
    fontStyle: 'italic',
    marginTop: '4px',
    lineHeight: '15px',
  },
  versionHistoryRestorePreview: {
    marginTop: '8px',
    background: '#f7f8fa',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    padding: '8px 10px',
    fontSize: '12px',
    color: '#404d5f',
  },
  versionHistoryRestorePreviewTitle: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#8899a6',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
    marginBottom: '6px',
  },
}));

export const useUploadedFilesStyles = makeStyles<Theme>(theme => ({
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    background: '#F8F8F8',
    borderRadius: '6px',
    border: `1px solid #E2E8F0`,
  },
  fileName: {
    flex: 1,
    fontSize: '14px',
    color: theme.palette.common.gray800,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chip: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '12px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  processing: { background: '#FFF8E1', color: '#F59E0B' },
  ready: { background: '#E8F5E9', color: '#16A34A' },
  failed: { background: '#FFEBEE', color: '#DC2626' },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#F59E0B',
    display: 'inline-block',
  },
  empty: {
    fontSize: '14px',
    color: '#757575',
    textAlign: 'center',
    padding: '24px 0',
    margin: 0,
  },
  fetchError: {
    color: '#DC2626',
  },
}));

export const useUploadScreenStyles = makeStyles<Theme>(theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    overflowY: 'auto',
    background: theme.palette.common.white,
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
  },
  description: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '20px',
    color: theme.palette.common.gray800,
    margin: 0,
  },
  helpLink: {
    display: 'block',
    fontSize: '16px',
    lineHeight: '20px',
    color: '#1864fd',
    textDecoration: 'underline',
    cursor: 'pointer',
    margin: 0,
  },
  dropZone: {
    border: `1px dashed ${theme.palette.common.gray500}`,
    borderRadius: '5px',
    height: '216px',
    width: '100%',
    position: 'relative',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover': {
      borderColor: '#1864fd',
      backgroundColor: '#f8f9ff',
    },
  },
  dropZoneActive: {
    borderColor: '#1864fd',
    backgroundColor: '#f8f9ff',
  },
  dropText: {
    position: 'absolute',
    left: '50%',
    top: 'calc(50% + 40px)',
    transform: 'translateX(-50%)',
    fontSize: '14px',
    lineHeight: '16px',
    color: theme.palette.common.gray800,
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
  chooseFilesLink: {
    color: theme.palette.common.blue400,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  dropSubtext: {
    position: 'absolute',
    left: '50%',
    top: 'calc(50% + 60px)',
    transform: 'translateX(-50%)',
    fontSize: '14px',
    lineHeight: '16px',
    color: theme.palette.common.gray600,
    whiteSpace: 'nowrap',
    textAlign: 'center',
  },
  selectedFileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    background: theme.palette.common.gray100,
    borderRadius: '5px',
    fontSize: '14px',
    color: theme.palette.common.gray800,
    flexShrink: 0,
  },
  selectedFileName: {
    flex: 1,
    fontWeight: 500,
    fontSize: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeFile: {
    cursor: 'pointer',
    color: theme.palette.common.gray500,
    background: 'none',
    border: 'none',
    padding: '0 4px',
    fontSize: '14px',
    lineHeight: 1,
    '&:hover': { color: theme.palette.common.gray800 },
  },
  skipBtn: {
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '16px',
    alignSelf: 'flex-start',
  },
  buttonRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignSelf: 'flex-start',
  },
  actionBtn: {
    textTransform: 'none' as const,
    fontWeight: 500,
    padding: '8px 24px',
    borderRadius: '5px',
  },
  dropZoneIconSvg: {
    position: 'absolute' as const,
    left: '50%',
    top: 'calc(50% - 24px)',
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  },
  uploadSubmitBtn: {
    alignSelf: 'flex-start' as const,
    textTransform: 'none' as const,
    fontWeight: 500,
  },
  uploadSubmitBtnSpinner: {
    color: theme.palette.common.white,
  },
}));
